import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_SURFACE, FONT_HEADING } from './designTokens';

// ── Types ─────────────────────────────────────────────────────

interface UnseatedGuest {
  id: string;
  fullName: string;
  ticketNumber: string | null;
  tierName: string | null;
  chapterAffiliation: string | null;
  dietaryPreference: string | null;
  accessibilityNeeds: string | null;
}

interface OrderGroup {
  orderId: string;
  referenceId: string;
  guestCount: number;
  primaryChapter: string | null;
  tier: string | null;
  guests: UnseatedGuest[];
}

interface TableSuggestion {
  tableId: string;
  tableName: string;
  tableNumber: number;
  zoneName: string;
  availableSeats: number;
  score: number;
  reasons: string[];
}

interface AssignmentLog {
  time: string;
  orderRef: string;
  guestCount: number;
  tableName: string;
  zoneName: string;
  guestIds: string[];
}

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', minHeight: '500px' },
  pane: { border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', backgroundColor: '#fff', padding: '0.75rem', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  paneTitle: { fontSize: '0.8rem', fontWeight: 700, color: PRIMARY, marginBottom: '0.5rem', textTransform: 'uppercase' as const, letterSpacing: '0.04em' },
  scrollArea: { flex: 1, overflowY: 'auto' as const, maxHeight: '450px' },
  orderCard: { padding: '0.5rem', borderRadius: '6px', border: `1px solid ${BORDER_LIGHT}`, marginBottom: '0.4rem', cursor: 'pointer', transition: 'background 0.15s' },
  orderCardSelected: { padding: '0.5rem', borderRadius: '6px', border: `2px solid ${PRIMARY}`, marginBottom: '0.4rem', cursor: 'pointer', backgroundColor: '#f0f7f2' },
  badge: { display: 'inline-block', padding: '0.1rem 0.4rem', borderRadius: '8px', fontSize: '0.65rem', fontWeight: 700, marginLeft: '0.3rem' },
  guestRow: { display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: '0.78rem' },
  rule: { padding: '0.3rem 0.5rem', borderRadius: '4px', fontSize: '0.72rem', marginBottom: '0.3rem', fontWeight: 500 },
  suggestionCard: { padding: '0.6rem', borderRadius: '6px', border: `1px solid ${BORDER_LIGHT}`, marginBottom: '0.5rem', backgroundColor: BG_SURFACE },
  acceptBtn: { padding: '0.3rem 0.7rem', fontSize: '0.72rem', fontWeight: 600, backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '0.3rem' },
  undoBtn: { background: 'none', border: 'none', color: '#dc2626', fontSize: '0.7rem', cursor: 'pointer', fontWeight: 600 },
  logRow: { padding: '0.35rem 0', borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: '0.75rem' },
  stats: { padding: '0.4rem 0.5rem', backgroundColor: BG_SURFACE, borderRadius: '6px', marginBottom: '0.5rem', fontSize: '0.72rem', color: TEXT_MUTED },
  empty: { textAlign: 'center' as const, color: TEXT_MUTED, fontSize: '0.8rem', padding: '1rem', fontStyle: 'italic' as const },
  loading: { textAlign: 'center' as const, color: TEXT_MUTED, fontSize: '0.8rem', padding: '1rem' },
};

// ── Component ─────────────────────────────────────────────────

export function AISeatingPanel() {
  const [orders, setOrders] = useState<OrderGroup[]>([]);
  const [singles, setSingles] = useState<UnseatedGuest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<OrderGroup | null>(null);
  const [suggestions, setSuggestions] = useState<TableSuggestion[]>([]);
  const [sugLoading, setSugLoading] = useState(false);
  const [assignLog, setAssignLog] = useState<AssignmentLog[]>([]);
  const [accepting, setAccepting] = useState(false);
  const [hoveredTableId, setHoveredTableId] = useState<string | null>(null);
  const [tableOccupants, setTableOccupants] = useState<Map<string, { name: string; seat: number; chapter: string }[]>>(new Map());

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient<{ orders: OrderGroup[]; singles: UnseatedGuest[] }>('/seating-intelligence/unseated-queue');
      setOrders(res.orders);
      setSingles(res.singles);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  // When an order is selected, fetch suggestions
  useEffect(() => {
    if (!selectedOrder) { setSuggestions([]); return; }
    setSugLoading(true);
    apiClient<{ suggestions: TableSuggestion[] }>('/seating-intelligence/suggest-table', {
      method: 'POST',
      body: {
        guestIds: selectedOrder.guests.map((g) => g.id),
        tier: selectedOrder.tier,
        chapter: selectedOrder.primaryChapter,
        guestCount: selectedOrder.guestCount,
      },
    })
      .then((res) => setSuggestions(res.suggestions || []))
      .catch(() => setSuggestions([]))
      .finally(() => setSugLoading(false));
  }, [selectedOrder]);

  // Fetch table occupants on hover
  const handleSuggestionHover = useCallback(async (tableId: string) => {
    setHoveredTableId(tableId);
    if (tableOccupants.has(tableId)) return; // Already cached
    try {
      const data = await apiClient<{ seats: Array<{ seatNumber: number; status: string; guest?: { fullName: string; chapterAffiliation?: string | null } | null }> }>(`/table-management/tables/${tableId}`);
      const occupied = (data.seats || [])
        .filter((s) => s.status === 'Assigned' || s.status === 'CheckedIn')
        .map((s) => ({ name: s.guest?.fullName || 'Unknown', seat: s.seatNumber, chapter: s.guest?.chapterAffiliation || '—' }))
        .sort((a, b) => a.seat - b.seat);
      setTableOccupants((prev) => new Map(prev).set(tableId, occupied));
    } catch { /* silent */ }
  }, [tableOccupants]);

  async function handleAccept(suggestion: TableSuggestion) {
    if (!selectedOrder) return;
    setAccepting(true);
    try {
      const res = await apiClient<{ success: boolean; assignments: Array<{ guestId: string; guestName: string; seatNumber: number }>; message: string }>(
        '/seating-intelligence/accept-suggestion',
        { method: 'POST', body: { guestIds: selectedOrder.guests.map((g) => g.id), tableId: suggestion.tableId } },
      );
      if (res.success) {
        setAssignLog((prev) => [{
          time: new Date().toLocaleTimeString(),
          orderRef: selectedOrder.referenceId,
          guestCount: res.assignments.length,
          tableName: suggestion.tableName,
          zoneName: suggestion.zoneName,
          guestIds: selectedOrder.guests.map((g) => g.id),
        }, ...prev]);
        // Remove from queue and auto-select next
        setOrders((prev) => prev.filter((o) => o.orderId !== selectedOrder.orderId));
        setSelectedOrder(null);
        setSuggestions([]);
      }
    } catch { /* silent */ }
    finally { setAccepting(false); }
  }

  async function handleUndo(log: AssignmentLog) {
    try {
      await apiClient('/seating-intelligence/undo-assignment', { method: 'POST', body: { guestIds: log.guestIds } });
      setAssignLog((prev) => prev.filter((l) => l !== log));
      fetchQueue(); // Refresh queue
    } catch { /* silent */ }
  }

  function handleSelectSingle(guest: UnseatedGuest) {
    // Wrap single guest as an order group
    setSelectedOrder({
      orderId: guest.id,
      referenceId: guest.ticketNumber || guest.fullName.slice(0, 8),
      guestCount: 1,
      primaryChapter: guest.chapterAffiliation,
      tier: guest.tierName,
      guests: [guest],
    });
  }

  const totalRemaining = orders.reduce((s, o) => s + o.guestCount, 0) + singles.length;
  const totalSeated = assignLog.reduce((s, l) => s + l.guestCount, 0);

  return (
    <div>
      <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ fontSize: '1rem', fontFamily: FONT_HEADING, fontWeight: 700, color: TEXT_DARK, margin: 0 }}>🤖 AI Seating Assistant</h3>
        <button style={{ padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, backgroundColor: BG_SURFACE, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', cursor: 'pointer', color: TEXT_DARK }} onClick={fetchQueue}>↻ Refresh</button>
      </div>

      <div style={styles.container}>
        {/* PANE 1: Unseated Queue */}
        <div style={styles.pane}>
          <div style={styles.paneTitle}>Unseated Queue ({totalRemaining})</div>
          <div style={styles.scrollArea}>
            {loading && <div style={styles.loading}>Loading...</div>}
            {!loading && orders.length === 0 && singles.length === 0 && <div style={styles.empty}>All guests seated!</div>}
            {orders.map((order) => (
              <div
                key={order.orderId}
                style={selectedOrder?.orderId === order.orderId ? styles.orderCardSelected : styles.orderCard}
                onClick={() => setSelectedOrder(order)}
              >
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: TEXT_DARK }}>
                  {order.referenceId}
                  <span style={{ ...styles.badge, backgroundColor: '#dbeafe', color: '#1e40af' }}>{order.guestCount} guests</span>
                  {order.tier === 'Patron' && <span style={{ ...styles.badge, backgroundColor: '#fef3c7', color: '#92400e' }}>Patron</span>}
                </div>
                <div style={{ fontSize: '0.68rem', color: TEXT_MUTED, marginTop: '0.15rem' }}>
                  {order.primaryChapter || 'No chapter'} · {order.guests.slice(0, 2).map((g) => g.fullName.split(' ')[0]).join(', ')}{order.guestCount > 2 ? ` +${order.guestCount - 2}` : ''}
                </div>
              </div>
            ))}
            {singles.length > 0 && (
              <>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: TEXT_MUTED, margin: '0.5rem 0 0.3rem', textTransform: 'uppercase' }}>Singles ({singles.length})</div>
                {singles.slice(0, 20).map((guest) => (
                  <div key={guest.id} style={selectedOrder?.orderId === guest.id ? styles.orderCardSelected : styles.orderCard} onClick={() => handleSelectSingle(guest)}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 500, color: TEXT_DARK }}>{guest.fullName}</div>
                    <div style={{ fontSize: '0.65rem', color: TEXT_MUTED }}>{guest.chapterAffiliation || '—'}{guest.tierName === 'Patron' ? ' · Patron' : ''}</div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* PANE 2: Order Details & Rules */}
        <div style={styles.pane}>
          <div style={styles.paneTitle}>Order Details</div>
          <div style={styles.scrollArea}>
            {!selectedOrder && <div style={styles.empty}>Select an order from the queue</div>}
            {selectedOrder && (
              <>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.5rem' }}>{selectedOrder.referenceId} — {selectedOrder.guestCount} guest(s)</div>
                {selectedOrder.guests.map((g) => (
                  <div key={g.id} style={styles.guestRow}>
                    <span style={{ fontWeight: 500 }}>{g.fullName}</span>
                    <span style={{ color: TEXT_MUTED }}>{g.tierName || 'General'}</span>
                  </div>
                ))}
                <div style={{ marginTop: '0.75rem', fontSize: '0.72rem', fontWeight: 600, color: TEXT_DARK }}>Rules & Insights</div>
                {selectedOrder.tier === 'Patron' && (
                  <div style={{ ...styles.rule, backgroundColor: '#fef2f2', color: '#dc2626' }}>🔴 Patron tier — must seat in VIP zone</div>
                )}
                {selectedOrder.primaryChapter && (
                  <div style={{ ...styles.rule, backgroundColor: '#ecfdf5', color: '#059669' }}>🟢 {selectedOrder.primaryChapter} chapter — prefer {selectedOrder.primaryChapter} zone</div>
                )}
                {selectedOrder.guests.some((g) => g.accessibilityNeeds) && (
                  <div style={{ ...styles.rule, backgroundColor: '#eff6ff', color: '#1e40af' }}>♿ Accessibility needs — prefer accessible table</div>
                )}
                <div style={{ ...styles.rule, backgroundColor: BG_SURFACE, color: TEXT_MUTED }}>📊 Group size: {selectedOrder.guestCount} — need table with {selectedOrder.guestCount}+ open seats</div>
              </>
            )}
          </div>
        </div>

        {/* PANE 3: AI Suggestions */}
        <div style={styles.pane}>
          <div style={styles.paneTitle}>AI Suggestions</div>
          <div style={styles.scrollArea}>
            {!selectedOrder && <div style={styles.empty}>Select an order to see suggestions</div>}
            {sugLoading && <div style={styles.loading}>Analyzing tables...</div>}
            {!sugLoading && selectedOrder && suggestions.length === 0 && <div style={styles.empty}>No tables available matching constraints</div>}
            {suggestions.map((sug, idx) => (
              <div
                key={sug.tableId}
                style={{ ...styles.suggestionCard, position: 'relative' }}
                onMouseEnter={() => handleSuggestionHover(sug.tableId)}
                onMouseLeave={() => setHoveredTableId(null)}
              >
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: TEXT_DARK }}>
                  {idx === 0 ? '🏆' : `#${idx + 1}`} {sug.tableName} ({sug.zoneName})
                </div>
                <div style={{ fontSize: '0.68rem', color: TEXT_MUTED, margin: '0.2rem 0' }}>
                  {sug.availableSeats} seats available · Score: {sug.score}
                </div>
                {sug.reasons.map((r, i) => (
                  <div key={i} style={{ fontSize: '0.68rem', color: '#374151', lineHeight: 1.4 }}>{r}</div>
                ))}
                <button style={styles.acceptBtn} onClick={() => handleAccept(sug)} disabled={accepting}>
                  {accepting ? '⏳ ...' : '✓ Accept'}
                </button>
                {/* Occupant tooltip */}
                {hoveredTableId === sug.tableId && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px', backgroundColor: '#1f2937', color: '#fff', padding: '8px 10px', borderRadius: '6px', fontSize: '0.7rem', lineHeight: '1.5', zIndex: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.25)', maxHeight: '180px', overflowY: 'auto' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#9ca3af' }}>Current Occupants</div>
                    {tableOccupants.has(sug.tableId) ? (
                      tableOccupants.get(sug.tableId)!.length > 0 ? (
                        tableOccupants.get(sug.tableId)!.map((o) => (
                          <div key={o.seat} style={{ display: 'flex', gap: '6px', padding: '1px 0' }}>
                            <span style={{ flex: 1 }}>{o.name}</span>
                            <span style={{ color: '#93c5fd', fontSize: '0.65rem' }}>{o.chapter}</span>
                            <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>S{o.seat}</span>
                          </div>
                        ))
                      ) : (
                        <div style={{ color: '#6ee7b7' }}>Table is empty</div>
                      )
                    ) : (
                      <div style={{ color: '#9ca3af' }}>Loading...</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* PANE 4: Assignment Log */}
        <div style={styles.pane}>
          <div style={styles.paneTitle}>Assignment Log</div>
          <div style={styles.stats}>
            Seated this session: <strong>{totalSeated}</strong> · Orders: <strong>{assignLog.length}</strong> · Remaining: <strong>{totalRemaining}</strong>
          </div>
          <div style={styles.scrollArea}>
            {assignLog.length === 0 && <div style={styles.empty}>No assignments yet</div>}
            {assignLog.map((log, idx) => (
              <div key={idx} style={styles.logRow}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600, color: TEXT_DARK }}>{log.orderRef}</span>
                  <button style={styles.undoBtn} onClick={() => handleUndo(log)}>Undo</button>
                </div>
                <div style={{ color: TEXT_MUTED, fontSize: '0.68rem' }}>
                  {log.guestCount} guests → {log.tableName} ({log.zoneName}) · {log.time}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
